import { useEffect, useRef, useState } from "react";
import { useAuth } from "oidc-react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getPushSubscriptionStatus,
  isPushNotificationSupported,
  subscribeToPushNotifications,
} from "../utils/pushNotifications";

const PUSH_PROMPT_STORAGE_KEY = "pushNotificationPromptState.v1";
const PROMPT_DELAY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const MAX_DECLINES = 3;

function readPromptState() {
  try {
    const storedValue = localStorage.getItem(PUSH_PROMPT_STORAGE_KEY);
    if (!storedValue) {
      return { declineCount: 0, nextPromptAt: null, completed: false };
    }

    const parsedValue = JSON.parse(storedValue);
    return {
      declineCount: Number(parsedValue.declineCount) || 0,
      nextPromptAt: parsedValue.nextPromptAt ?? null,
      completed: Boolean(parsedValue.completed),
    };
  } catch (error) {
    console.log("Failed to read push prompt state: " + error);
    return { declineCount: 0, nextPromptAt: null, completed: false };
  }
}

function writePromptState(state) {
  localStorage.setItem(PUSH_PROMPT_STORAGE_KEY, JSON.stringify(state));
}

function shouldPromptUser(state) {
  if (state.completed) {
    return false;
  }

  if (!state.nextPromptAt) {
    return true;
  }

  return Date.now() >= new Date(state.nextPromptAt).getTime();
}

function recordDecline() {
  const state = readPromptState();
  const declineCount = state.declineCount + 1;

  if (declineCount >= MAX_DECLINES) {
    writePromptState({
      declineCount,
      nextPromptAt: null,
      completed: true,
    });
    return;
  }

  writePromptState({
    declineCount,
    nextPromptAt: new Date(Date.now() + PROMPT_DELAY_MS).toISOString(),
    completed: false,
  });
}

function clearPromptState() {
  localStorage.removeItem(PUSH_PROMPT_STORAGE_KEY);
}

export default function PushNotificationPrompt() {
  const auth = useAuth();
  const accessToken = auth.userData?.access_token;
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasCheckedForToken = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function checkPromptEligibility() {
      if (!accessToken || hasCheckedForToken.current === accessToken) {
        return;
      }

      hasCheckedForToken.current = accessToken;

      if (!isPushNotificationSupported()) {
        return;
      }

      const promptState = readPromptState();
      if (!shouldPromptUser(promptState)) {
        return;
      }

      try {
        const status = await getPushSubscriptionStatus();
        if (!isMounted) {
          return;
        }

        if (status.isSubscribed || status.notificationsBlocked) {
          return;
        }

        setRegistration(status.registration);
        setDialogOpen(true);
      } catch (error) {
        if (isMounted) {
          console.log(
            "Failed to initialize push notification prompt: " + error
          );
        }
      }
    }

    checkPromptEligibility();

    return () => {
      isMounted = false;
    };
  }, [accessToken]);

  const handleDecline = () => {
    recordDecline();
    setDialogOpen(false);
  };

  const handleAccept = async () => {
    if (!accessToken || !registration) {
      return;
    }

    setIsSubmitting(true);

    try {
      await subscribeToPushNotifications({ accessToken, registration });
      clearPromptState();
      setDialogOpen(false);
    } catch (error) {
      console.log("Failed to subscribe for Push Notifications: " + error);
      if (Notification.permission === "denied") {
        setDialogOpen(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={dialogOpen} disableEscapeKeyDown>
      <DialogTitle>{t("notificationSettings.prompt.title")}</DialogTitle>
      <DialogContent>
        <Typography>{t("notificationSettings.prompt.question")}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDecline} disabled={isSubmitting} color="inherit">
          {t("notificationSettings.prompt.no")}
        </Button>
        <Button
          onClick={handleAccept}
          disabled={isSubmitting}
          variant="contained"
        >
          {t("notificationSettings.prompt.yes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
